import {
  type RetailerScopedRouteParams,
  type PartnerScopedRouteParams,
} from '../../legacy/common/types'
import { LEGACY_ROUTE_URLS } from '../../legacy/common/routes'

export type DashRouteType<TIdentifier extends string, TParams extends {} = {}> = {
  identifier: TIdentifier
  params: TParams
}

/* --------------------------------------------- */
/* --------------------------------------------- */
/*        FREE-FORM ROUTING URLS                 */
/* --------------------------------------------- */
/* --------------------------------------------- */

export const URL_BASE = '/'

/* ------------------------- */
/*      PUBLIC ROUTES        */
/* ------------------------- */

// PUBLIC ROUTES
export const URL_LOGOUT = '/logout'
export const URL_STRIPE_CONNECT_CALLBACK = '/stripe'
export const URL_SCOPE_SELECTOR = '/scope'

/* ------------------------- */
/*   PARTNER SCOPED ROUTES   */
/* ------------------------- */

export const URL_PARTNER_BASE = '/partners'
export const URL_PARTNER_SCOPED = `${URL_PARTNER_BASE}/:partner_id`
export const URL_PARTNER_HALT_TERMS = `${URL_PARTNER_SCOPED}/terms-conditions`

/* ------------------------- */
/*  RETAILER SCOPED ROUTES   */
/* ------------------------- */

export const URL_RETAILER_SCOPED = `${URL_PARTNER_SCOPED}/warehouses/:warehouse_id`
export const URL_DASHBOARD = `${URL_RETAILER_SCOPED}/dashboard`
export const URL_SETTINGS = `${URL_RETAILER_SCOPED}/settings`
export const URL_STORE_GROUPS = `${URL_RETAILER_SCOPED}/store-groups`
const URL_ORDERS = `${URL_RETAILER_SCOPED}/orders`
const URL_RETAILER_FILES = `${URL_RETAILER_SCOPED}/retailer-files`

/* --------------------- */
/*  SITE SCOPED ROUTES   */
/* --------------------- */

export const URL_SITE_SCOPED = `/sites/:storeConfigId`

// Notifications Routers
const URL_BANNER_NOTIFICATIONS_LIST = `${URL_RETAILER_SCOPED}/notifications/list`
const URL_PLATFORM_NOTIFICATIONS_LIST = `${URL_RETAILER_SCOPED}/notifications/platform/list`
const URL_NOTIFICATION_SETTINGS = `${URL_PARTNER_SCOPED}/notifications/settings`

/* ------------------------------- */
/*       PUBLIC / APP ROUTES       */
/* ------------------------------- */

// Public Routes
const URL_LOG_OUT = `/log-out`
export const URL_PUBLIC = `/public`

// App Routes (Protected Routes, Non-Public)
export const URL_APP = `/app`

const URL_APP_HALT = `${URL_APP}/halt`
const URL_APP_HALT_REDIRECT = `${URL_APP_HALT}/redirect`
const URL_APP_HALT_ACCOUNT_ACTION_REQUIRED = `${URL_APP_HALT}/account-action-required`
const URL_APP_HALT_ACCOUNT_LIMITED_ACCESS = `${URL_APP_HALT}/account-limited-access`
const URL_APP_HALT_AUTH_SERVICE_ACCOUNT_LIMITED_ACCESS = `${URL_APP_HALT}/auth-service-account-limited-access`

export const URL_APP_ADMIN = `${URL_APP}/admin`

// Landing Page System Routes
export const URL_APP_ADMIN_LANDING_PAGE_SYSTEM = `${URL_APP_ADMIN}/lps`

// Notice Banners (admin)
export const URL_ADMIN_NOTICE_BANNERS = `${URL_APP_ADMIN}/partner-settings/notice-banners`
export const URL_ADMIN_NOTICE_BANNERS_NEW = `${URL_ADMIN_NOTICE_BANNERS}/new`
export const URL_ADMIN_NOTICE_BANNERS_EDIT = `${URL_ADMIN_NOTICE_BANNERS}/:id/edit`

/* --------------------------------------------- */
/* --------------------------------------------- */
/*            REGISTER ROUTES                    */
/* --------------------------------------------- */
/* --------------------------------------------- */

export type DashRouteSet =
  | DashRouteType<'base'>
  | DashRouteType<'logout'>
  | DashRouteType<'stripe-connect-callback'>
  | DashRouteType<'scope'>
  | DashRouteType<'partner-scoped', PartnerScopedRouteParams>
  | DashRouteType<'retailer-scoped', RetailerScopedRouteParams>
  | DashRouteType<'orders', RetailerScopedRouteParams>
  | DashRouteType<'settings', RetailerScopedRouteParams>
  | DashRouteType<'store-groups', RetailerScopedRouteParams>
  | DashRouteType<'retailer-files', RetailerScopedRouteParams>
  | DashRouteType<'log-out'>
  | DashRouteType<'public'>
  | DashRouteType<'app'>
  | DashRouteType<'app-halt'>
  | DashRouteType<'app-halt-redirect'>
  | DashRouteType<'app-halt-account-limited-access'>
  | DashRouteType<'app-halt-auth-service-account-limited-access'>
  | DashRouteType<'app-halt-account-action-required'>
  | DashRouteType<'app-admin'>
  | DashRouteType<'admin-notice-banners'>
  | DashRouteType<'admin-notice-banners-new'>
  | DashRouteType<'admin-notice-banners-edit', { id: string }>
  | DashRouteType<'banner-notifications-list'>
  | DashRouteType<'platform-notifications-list'>
  | DashRouteType<'partner-halt-terms', PartnerScopedRouteParams>
  | DashRouteType<'dashboard', RetailerScopedRouteParams>
  | DashRouteType<'notification-settings'>

export type DashRouteName = DashRouteSet['identifier']

/* --------------------------------------------- */
/* --------------------------------------------- */
/*         WIRE UP ROUTES TO URLS                */
/* --------------------------------------------- */
/* --------------------------------------------- */

export const DASH_ROUTE_URLS: { [Key in DashRouteSet['identifier']]: string } = {
  base: URL_BASE,
  logout: URL_LOGOUT,
  'stripe-connect-callback': URL_STRIPE_CONNECT_CALLBACK,
  scope: URL_SCOPE_SELECTOR,
  'partner-scoped': URL_PARTNER_SCOPED,
  'retailer-scoped': URL_RETAILER_SCOPED,
  orders: URL_ORDERS,
  settings: URL_SETTINGS,
  'store-groups': URL_STORE_GROUPS,
  'retailer-files': URL_RETAILER_FILES,
  'log-out': URL_LOG_OUT,
  public: URL_PUBLIC,
  app: URL_APP,
  'app-halt': URL_APP_HALT,
  'app-halt-redirect': URL_APP_HALT_REDIRECT,
  'partner-halt-terms': URL_PARTNER_HALT_TERMS,
  'app-halt-account-limited-access': URL_APP_HALT_ACCOUNT_LIMITED_ACCESS,
  'app-halt-auth-service-account-limited-access': URL_APP_HALT_AUTH_SERVICE_ACCOUNT_LIMITED_ACCESS,
  'app-halt-account-action-required': URL_APP_HALT_ACCOUNT_ACTION_REQUIRED,
  'app-admin': URL_APP_ADMIN,
  'admin-notice-banners': URL_ADMIN_NOTICE_BANNERS,
  'admin-notice-banners-new': URL_ADMIN_NOTICE_BANNERS_NEW,
  'admin-notice-banners-edit': URL_ADMIN_NOTICE_BANNERS_EDIT,
  'banner-notifications-list': URL_BANNER_NOTIFICATIONS_LIST,
  'platform-notifications-list': URL_PLATFORM_NOTIFICATIONS_LIST,
  dashboard: URL_DASHBOARD,
  'notification-settings': URL_NOTIFICATION_SETTINGS,
}

export const ROUTES_BY_NAME = { ...DASH_ROUTE_URLS, ...LEGACY_ROUTE_URLS }
export type RouteName = keyof typeof ROUTES_BY_NAME

const ROUTE_NAMES = Object.keys(ROUTES_BY_NAME) as RouteName[]
export const ROUTES_BY_PATH = ROUTE_NAMES.reduce<{ [key: string]: RouteName }>((acc, key) => {
  acc[ROUTES_BY_NAME[key]] = key
  return acc
}, {})
